// Push Notification Hook for Admin Panel
import { useEffect, useState, useCallback, useRef } from 'react';
import { 
  initializeFirebase, 
  requestNotificationPermission, 
  onForegroundMessage,
  registerServiceWorker,
  playNotificationSound
} from '../lib/firebase';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const initRef = useRef(false);

  // Register FCM token with backend
  const registerToken = useCallback(async (fcmToken) => {
    try {
      const adminToken = localStorage.getItem('vipgez_admin_token') || localStorage.getItem('admin_token');
      if (!adminToken) {
        console.log('No admin token found');
        return false;
      }

      const response = await fetch(`${API_URL}/api/notifications/register-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          token: fcmToken,
          device_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            registeredAt: new Date().toISOString()
          }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('FCM token registered with backend');
        setIsRegistered(true);
        return true;
      } else {
        console.error('Failed to register token:', data.error);
        return false;
      }
    } catch (err) {
      console.error('Error registering token:', err);
      return false;
    }
  }, []);

  // Auto-register if permission already granted
  useEffect(() => {
    const autoRegister = async () => {
      if (initRef.current) return;
      
      const supported = 'Notification' in window && 'serviceWorker' in navigator;
      setIsSupported(supported);
      
      if (!supported) return;
      
      // Check if Firebase is configured before attempting auto-register
      const firebaseConfigured = process.env.REACT_APP_FIREBASE_API_KEY && 
                                  process.env.REACT_APP_FIREBASE_PROJECT_ID &&
                                  process.env.REACT_APP_FIREBASE_VAPID_KEY;
      
      if (!firebaseConfigured) {
        // Firebase not configured - silently disable push notifications
        return;
      }
      
      const currentPermission = Notification.permission;
      setPermission(currentPermission);
      
      // If permission already granted, auto-register token
      if (currentPermission === 'granted') {
        initRef.current = true;
        console.log('Permission already granted, auto-registering...');
        
        try {
          await registerServiceWorker();
          await initializeFirebase();
          const result = await requestNotificationPermission();
          
          if (result.success && result.token) {
            setToken(result.token);
            await registerToken(result.token);
            console.log('Auto-registration successful');
          } else if (!result.notConfigured) {
            console.log('Auto-registration failed:', result.error);
          }
        } catch (err) {
          console.error('Auto-registration error:', err);
        }
      }
    };
    
    autoRegister();
  }, [registerToken]);

  // Enable push notifications (manual trigger)
  const enableNotifications = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      toast.error('Bu tarayıcı bildirimleri desteklemiyor');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Register service worker first
      await registerServiceWorker();
      
      // Initialize Firebase and request permission
      await initializeFirebase();
      const result = await requestNotificationPermission();
      
      console.log('Permission result:', result);
      
      if (result.success && result.token) {
        setToken(result.token);
        setPermission('granted');
        
        // Register token with backend
        const registered = await registerToken(result.token);
        
        if (registered) {
          toast.success('Bildirimler aktif edildi!', {
            description: 'Yeni siparişlerde bildirim alacaksınız.'
          });
        } else {
          toast.error('Token kaydedilemedi. Lütfen tekrar deneyin.');
        }
        
        return registered;
      } else {
        setError(result.error || 'Failed to enable notifications');
        setPermission(Notification.permission);
        toast.error(result.error || 'Bildirim izni alınamadı');
        return false;
      }
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError(err.message);
      toast.error('Bildirim aktifleştirme hatası: ' + err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, registerToken]);

  // Setup foreground message listener
  useEffect(() => {
    if (permission !== 'granted' || !token) return;

    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Foreground message received:', payload);
      
      // Play notification sound
      playNotificationSound();
      
      // Show toast notification
      const title = payload.notification?.title || 'Yeni Sipariş!';
      const body = payload.notification?.body || 'Yeni bir sipariş var.';
      
      toast.success(title, {
        description: body,
        duration: 10000,
        action: {
          label: 'Görüntüle',
          onClick: () => {
            window.location.href = '/admin/bookings';
          }
        }
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [permission, token]);

  // Disable push notifications
  const disableNotifications = useCallback(async () => {
    if (!token) return;

    try {
      const adminToken = localStorage.getItem('vipgez_admin_token') || localStorage.getItem('admin_token');
      if (!adminToken) return;

      await fetch(`${API_URL}/api/notifications/unregister-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ token })
      });

      setToken(null);
      setIsRegistered(false);
      toast.info('Bildirimler kapatıldı');
    } catch (err) {
      console.error('Error disabling notifications:', err);
    }
  }, [token]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      const adminToken = localStorage.getItem('vipgez_admin_token') || localStorage.getItem('admin_token');
      if (!adminToken) {
        toast.error('Giriş yapmanız gerekiyor');
        return;
      }

      const response = await fetch(`${API_URL}/api/notifications/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          title: '🔔 Test Bildirimi',
          body: 'Bu bir test bildirimidir. Bildirimler çalışıyor!'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Test bildirimi gönderildi!');
      } else {
        toast.error(data.error || 'Test bildirimi gönderilemedi');
      }
    } catch (err) {
      console.error('Error sending test notification:', err);
      toast.error('Test bildirimi gönderilemedi');
    }
  }, []);

  return {
    isSupported,
    permission,
    token,
    isLoading,
    error,
    isRegistered,
    enableNotifications,
    disableNotifications,
    sendTestNotification
  };
};

export default usePushNotifications;
